import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { runChatExport } from '../../services/chatExport';
import { EXPORT_FORMAT_ORDER } from '../../utils/chatExportModel';
import './ChatSearch.css';

// Brand / format names are constants in every language (D21); "Plain text" is
// ordinary words and is translated.
const FORMAT_NAMES = { markdown: 'Markdown', json: 'JSON', html: 'HTML' };

/**
 * "Export chat history" — pick a format, then page the export endpoint and
 * save the file (services/chatExport). Closing the dialog while it runs
 * cancels the export; nothing is saved.
 */
export default function ChatExportDialog({ isOpen, onClose, channel, channelName }) {
  const { t } = useTranslation();
  const [format, setFormat] = useState('markdown');
  const [running, setRunning] = useState(false);
  const [count, setCount] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setRunning(false);
    setCount(0);
    return () => controllerRef.current?.abort();
  }, [isOpen]);

  const close = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setRunning(false);
    onClose();
  };

  const start = async () => {
    if (running) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    setRunning(true);
    setCount(0);
    const res = await runChatExport({
      channel,
      channelName,
      format,
      signal: controller.signal,
      onProgress: (n) => {
        if (controller.signal.aborted) return;
        setCount(n);
        setWaiting(false);
      },
      onRateLimited: () => !controller.signal.aborted && setWaiting(true),
      t,
    });
    if (controller.signal.aborted) return;
    controllerRef.current = null;
    setRunning(false);
    if (res.status !== 'error') onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={t('chat.export.dialogTitle')}>
      <div className="chat-export">
        <div className="chat-export-label" id="chat-export-format-label">
          {t('chat.export.formatLabel')}
        </div>
        <div className="chat-export-formats" role="radiogroup" aria-labelledby="chat-export-format-label">
          {EXPORT_FORMAT_ORDER.map((f) => (
            <label key={f} className={`chat-export-format${format === f ? ' chat-export-format--on' : ''}`}>
              <input
                type="radio"
                className="checkbox-brand"
                name="chat-export-format"
                value={f}
                checked={format === f}
                disabled={running}
                onChange={() => setFormat(f)}
              />
              <span className="chat-export-format-text">
                <span className="chat-export-format-name">{FORMAT_NAMES[f] || t(`chat.export.fmtName.${f}`)}</span>
                <span className="chat-export-format-desc">{t(`chat.export.fmt.${f}`)}</span>
              </span>
            </label>
          ))}
        </div>
        <p className="chat-export-note">{t('chat.export.attachmentsNote')}</p>
        {running && (
          <p className="chat-export-progress" role="status">
            {waiting ? t('chat.export.rateLimited') : t('chat.export.progress', { count })}
          </p>
        )}
        <div className="chat-export-actions">
          <button
            type="button"
            onClick={close}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('chat.export.cancel')}
          </button>
          <button
            type="button"
            onClick={start}
            disabled={running}
            className="px-3 py-1.5 rounded-lg transition-colors modern-button text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {t('chat.export.start')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
